import { useEffect, useState } from "react";
import type { District } from "./districts";
import { useWorldExperience } from "./WorldExperienceProvider";
import { staticVisualForPath } from "./visualPack";

interface StaticWorldProps {
  district: District;
  pathname: string;
  onFailure: () => void;
}

export function StaticWorld({
  district,
  pathname,
  onFailure,
}: StaticWorldProps) {
  const [failed, setFailed] = useState(false);
  const [legacyFallback, setLegacyFallback] = useState(false);
  const { updateDiagnostics } = useWorldExperience();

  useEffect(() => {
    setFailed(false);
    setLegacyFallback(false);
  }, [district.districtId, pathname]);

  const scene = staticVisualForPath(pathname, district, legacyFallback);

  useEffect(() => {
    updateDiagnostics({
      assetRequest: scene.desktopUrl,
      visualPackId: scene.packId,
      visualAssetId: scene.assetId,
      staticImageFailed: false,
    });
  }, [scene.assetId, scene.desktopUrl, scene.packId, updateDiagnostics]);

  if (failed) return null;

  return (
    <figure
      className="static-world"
      data-district={district.districtId}
      data-scene={district.sceneId}
      data-visual-pack={scene.packId}
      data-visual-asset={scene.assetId}
      data-visual-fallback={legacyFallback ? "legacy" : "none"}
      aria-label={district.description}
    >
      <picture>
        {scene.mobileUrl ? (
          <source media="(max-width: 700px)" srcSet={scene.mobileUrl} />
        ) : null}
        <img
          key={`${scene.packId}:${scene.assetId}`}
          src={scene.desktopUrl}
          alt=""
          decoding="async"
          onError={() => {
            updateDiagnostics({
              visualPackId: scene.packId,
              visualAssetId: scene.assetId,
              staticImageFailed: true,
            });
            if (scene.packId === "owner-approved-v1" && !legacyFallback) {
              setLegacyFallback(true);
              return;
            }
            setFailed(true);
            onFailure();
          }}
        />
      </picture>
      <figcaption className="sr-only">
        Decorative {scene.label} scene for {district.districtLabel}. It contains
        no controls or essential information.
      </figcaption>
    </figure>
  );
}
